import type { ReactNode } from 'react'

/**
 * The rows inside a `⋯` menu, and the line that groups them.
 *
 * Two boards have one of these — a column heading on Contacts, a segment on
 * Segments — and the items are the same thing in both: a full-width button that
 * reads as a menu row, a danger tone for the one that destroys something, and a
 * tick for the option currently in force. Written twice, the second copy is the
 * one whose danger row stops being red.
 *
 * The menu itself is `Popover`, which owns the opening and the click-away. This
 * is only what goes inside one.
 */

export function MenuItem({
  children,
  chosen,
  danger,
  onClick,
}: {
  readonly children: ReactNode
  /** The option currently in force, which gets the tick. */
  readonly chosen?: boolean
  readonly danger?: boolean
  readonly onClick: () => void
}) {
  const tone = danger === true ? 'text-danger hover:bg-danger-soft' : 'text-t1 hover:bg-line-2'

  return (
    <button
      className={`flex w-full items-center justify-between gap-2 rounded-field px-2 py-1.5 text-left font-medium text-[13px] normal-case tracking-normal ${tone}`}
      onClick={onClick}
      type="button"
    >
      {children}
      {chosen === true ? <span aria-hidden="true">✓</span> : null}
    </button>
  )
}

/** Separates one group of items from the next. */
export function MenuRule() {
  return <span className="my-1 block border-line border-t" />
}
